import React, { useState, useEffect, useRef } from 'react';
import { Input, AutoComplete, Tag as AntTag, message, Modal, Spin } from 'antd';
import { Tag as TagIcon } from 'lucide-react';
import styles from '../index.module.css';
import { v4 as uuid } from 'uuid';
import * as translations from '../strings';
import { getLangKey } from 'appJS/models/Language';
import { Website } from '../types';
import { put } from 'appJS/utils/fetch';
import { cleanUrl } from './ProjectSite';
import { useProjectSitesState } from '../../../../clients/providers/ProjectSites';
import { LoadingOutlined } from '@ant-design/icons';

const strings = translations[getLangKey()];

type Props = {
  site: Website;
  canEditLabels: boolean;
  allLabels: string[];
  projectId: string | number;
  open: boolean;
  setOpen: (open: boolean) => void;
  container: HTMLDivElement;
};

export const SiteLabels = ({
  site,
  allLabels,
  canEditLabels,
  projectId,
  open,
  setOpen,
  container
}: Props) => {
  const [value, setValue] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [newLabel, setNewLabel] = useState<string>('');
  const [loading, setLoading] = useState<string | boolean>(false);
  // @ts-expect-error
  const inputRef = useRef<Input>(null);

  const {
    handleGetProjectSites,
    isProjectSitesLoading
  } = useProjectSitesState();

  useEffect(() => {
    if (!newLabel) return;
    if (site.labels.includes(newLabel)) {
      setNewLabel('');
      setValue('');
    }
  }, [site.labels]);

  const updateLabels = async (labels: string[], updateType: string) => {
    if (!canEditLabels) return;
    setLoading(updateType === 'adding' || updateType);
    try {
      await put(`/projects/${projectId}/project_sites/${site.id}`, { labels });
      handleGetProjectSites();
      message.success('Labels updated successfully');
    } catch (error) {
      message.error('Failed to update labels');
      console.error('Failed to update labels:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isProjectSitesLoading && loading) {
      setLoading(false);
    }
  }, [isProjectSitesLoading]);

  const onRemoveLabel = (label: string) => {
    if (!site.labels.includes(label)) return;

    updateLabels(
      site.labels.filter((l: string) => l !== label),
      label
    );
  };

  const handleOnBlur = () => {
    if (value && !site.labels.includes(value)) {
      setNewLabel(value);
      updateLabels([...site.labels, value], 'adding');
    }
    setValue('');
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e?.key === 'Escape') {
      setValue('');
    } else if (e?.key === 'Enter') {
      handleOnBlur();
    }
  };

  const handleOnSelect = (selectedLabel: string) => {
    if (site.labels.includes(selectedLabel)) return;
    updateLabels([...site.labels, selectedLabel], 'adding');
    setValue('');
  };

  const labels =
    loading === true && newLabel ? [newLabel, ...site.labels] : site.labels;

  return (
    <Modal
      title={
        <>
          <TagIcon className={styles.labelIconHeader} />
          {strings.websiteLabels}
        </>
      }
      open={open}
      onCancel={event => {
        event.stopPropagation();
        setOpen(false);
      }}
      rootClassName={styles.labelsModalRoot}
      width={600}
      footer={null}
      className={styles.labelsModal}
      centered
      getContainer={container}
    >
      <div onClick={event => event.stopPropagation()}>
        <p className={styles.labelsTitle}>
          {strings.addALabel}
          <strong>{cleanUrl(site.url)}</strong>
        </p>
        <AutoComplete
          className={styles.labelsAutoComplete}
          getPopupContainer={trigger => trigger.parentElement}
          options={allLabels
            .filter(label => !site.labels.includes(label))
            .map(label => ({
              value: label,
              label: (
                <div key={`${label}-${uuid()}`} className={styles.optionLabel}>
                  <TagIcon className={styles.labelIcon} />
                  <span className={styles.labelName}>{label}</span>
                </div>
              )
            }))}
          value={value}
          onChange={setValue}
          popupMatchSelectWidth
          onSelect={handleOnSelect}
          onDropdownVisibleChange={setShowDropdown}
          open={showDropdown}
        >
          <Input
            ref={inputRef}
            className={styles.labelsInput}
            type="text"
            onKeyDown={handleOnKeyDown}
            placeholder={strings.searchOrNewLabel}
          />
        </AutoComplete>
        <div className={styles.labelsWrapper}>
          {labels.map((label: string, index: number) => (
            <span key={uuid()} className={styles.labelHolder}>
              <AntTag
                className={styles.label}
                closable={
                  canEditLabels && loading !== label && label !== newLabel
                }
                onClose={() => onRemoveLabel(label)}
              >
                <span className={styles.labelContent}>
                  {loading !== label && label !== newLabel ? (
                    <TagIcon className={styles.labelIcon} />
                  ) : (
                    <Spin
                      indicator={<LoadingOutlined spin />}
                      className={styles.spinIcon}
                      size="small"
                    />
                  )}
                  <span className={styles.innerLabel}>{label}</span>
                </span>
              </AntTag>
            </span>
          ))}
        </div>
      </div>
    </Modal>
  );
};
